// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { ActionIcon, Button, Card, Grid, Group, Radio, Stack, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import type { Condition, Patient } from '@medplum/fhirtypes';
import { CodingInput, useMedplum } from '@medplum/react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { buildIcd10Concept, ICD10_VALUESET } from '../../utils/icd10';
import { showErrorNotification } from '../../utils/notifications';
import { DiagnosisCodeInput } from './DiagnosisCodeInput';
import { type ChartFormValues, MIN_DIAGNOSIS_ENTRIES } from './encounter-chart-types';

interface DiagnosesSectionProps {
  patient: Patient;
  form: UseFormReturnType<ChartFormValues>;
  disabled?: boolean;
  codeDisabled?: boolean;
}

function getDiagnosisInputKey(
  id: string | undefined,
  index: number,
  code: string | undefined,
  display: string | undefined
): string {
  return `${id ?? `new:${index}`}:${code ?? ''}:${display ?? ''}`;
}

function getDiagnosisLabel(index: number): string {
  if (index === 0) {
    return 'Primary Diagnosis';
  }
  if (index === 1) {
    return 'Additional Diagnosis';
  }
  return `Additional Diagnosis ${index}`;
}

export const DiagnosesSection = ({
  form,
  disabled = false,
  codeDisabled = disabled,
}: DiagnosesSectionProps): JSX.Element => {
  const medplum = useMedplum();
  const setFieldValue = form.setFieldValue;

  useEffect(() => {
    const load = async (): Promise<void> => {
      const loadedDiagnoses = await Promise.all(
        form.values.diagnoses.map(async (diagnosis) => {
          if (!diagnosis.id || diagnosis.concept) {
            return diagnosis;
          }

          const condition = await medplum.readResource('Condition', diagnosis.id);
          return {
            ...diagnosis,
            concept: condition.code as Condition['code'] | undefined,
            recordedDate: condition.recordedDate,
          };
        })
      );

      const changed = loadedDiagnoses.some(
        (diagnosis, index) => diagnosis.concept !== form.values.diagnoses[index]?.concept
      );
      if (changed) {
        setFieldValue('diagnoses', loadedDiagnoses);
      }
    };
    load().catch(showErrorNotification);
  }, [form.values.diagnoses, setFieldValue, medplum]);

  return (
    <Card withBorder p={20}>
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>Diagnoses</Title>
          <Button
            disabled={disabled}
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={() =>
              form.insertListItem('diagnoses', {
                concept: undefined,
                recordedDate: new Date().toISOString().slice(0, 10),
              })
            }
          >
            Add Diagnosis
          </Button>
        </Group>
        <Stack gap="md">
          {form.values.diagnoses.map((diagnosis, index) => {
            const label = getDiagnosisLabel(index);
            const codeLabel = `${label} Code`;
            return (
              <Grid
                key={getDiagnosisInputKey(
                  diagnosis.id,
                  index,
                  diagnosis.concept?.coding?.[0]?.code,
                  diagnosis.concept?.coding?.[0]?.display ?? diagnosis.concept?.text
                )}
              >
                <Grid.Col span={5}>
                  <CodingInput
                    name={`diagnoses.${index}`}
                    path="Condition.code"
                    label={label}
                    binding={ICD10_VALUESET}
                    disabled={codeDisabled}
                    defaultValue={diagnosis.concept?.coding?.[0]}
                    onChange={(coding) => {
                      const display = coding?.display || coding?.code;
                      form.setFieldValue(`diagnoses.${index}`, {
                        id: form.values.diagnoses[index]?.id,
                        concept: coding ? { coding: [{ ...coding, display }], text: display } : undefined,
                      });
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={5}>
                  <DiagnosisCodeInput
                    disabled={disabled}
                    label={codeLabel}
                    value={diagnosis.concept?.coding?.[0]?.code}
                    onResolve={(coding) => {
                      form.setFieldValue(`diagnoses.${index}`, {
                        ...form.values.diagnoses[index],
                        concept: buildIcd10Concept(coding),
                      });
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={5}>
                  <DateInput
                    clearable
                    disabled={disabled}
                    label="Diagnosis Date"
                    value={diagnosis.recordedDate ?? null}
                    onChange={(date: string | null) =>
                      form.setFieldValue(`diagnoses.${index}.recordedDate`, date ?? undefined)
                    }
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <ActionIcon
                    aria-label={`Remove ${label}`}
                    color="red"
                    disabled={disabled || form.values.diagnoses.length <= MIN_DIAGNOSIS_ENTRIES}
                    mt={30}
                    variant="subtle"
                    onClick={() => form.removeListItem('diagnoses', index)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>

        <Radio.Group
          label="Suspected Positional Apnea?"
          value={form.values.suspectedPositionalApnea}
          onChange={(value) => form.setFieldValue('suspectedPositionalApnea', value)}
        >
          <Grid mt={4}>
            <Grid.Col span="content">
              <Radio value="yes" label="Yes" disabled={disabled} />
            </Grid.Col>
            <Grid.Col span="content">
              <Radio value="no" label="No" disabled={disabled} />
            </Grid.Col>
          </Grid>
        </Radio.Group>
      </Stack>
    </Card>
  );
};
